"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Footer from "@/components/marketing/footer";

export default function LawFirmsPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const show = (id: string) => visibleSections.has(id);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", scrollBehavior: "smooth" }}>
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* ─── NAV ─── */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 40 ? "bg-white shadow-md py-1" : "bg-white/90 py-1.5"
      }`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Contract Lucidity" className="h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/features" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Features</Link>
            <Link href="/how-it-works" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</Link>
            <Link href="/law-firms" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Law Firms</Link>
            <Link href="/in-house" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>In-House</Link>
            <Link href="/security" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Security</Link>
            <Link href="/why-contract-lucidity" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Why Us</Link>
            <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C8A84B] hover:text-[#b8993f] transition-colors font-semibold" style={{ fontFamily: "system-ui, sans-serif" }}>Lucid Vault</a>
            <Link
              href="/login"
              className="px-6 py-2.5 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-[#2E5EA0] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Sign In
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" style={{ fontFamily: "system-ui, sans-serif" }}>
            <div className="flex flex-col px-6 py-4 gap-3">
              <Link href="/features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Features</Link>
              <Link href="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">How It Works</Link>
              <Link href="/law-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Law Firms</Link>
              <Link href="/in-house" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">In-House</Link>
              <Link href="/security" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Security</Link>
              <Link href="/why-contract-lucidity" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Why Us</Link>
              <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="text-sm text-[#C8A84B] font-semibold hover:text-[#b8993f] py-2 border-b border-gray-50">Lucid Vault</a>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-white bg-[#1B3A6B] rounded px-4 py-2.5 text-center mt-2">Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative pt-20 min-h-[520px] md:min-h-[600px] flex items-center">
        <img
          src="/images/law-firms-hero.webp"
          alt="Law firm conference room"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A6B]/90 via-[#1B3A6B]/75 to-[#1B3A6B]/50" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-24">
          <div className="max-w-3xl">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-6" style={{ fontFamily: "system-ui, sans-serif" }}>
              For Law Firms
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.15] mb-6 text-white">
              Your associates produce partner-quality reviews{" "}
              <span className="text-[#C8A84B]">from day one.</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-2xl" style={{ fontFamily: "system-ui, sans-serif" }}>
              Contract Lucidity captures your firm&apos;s institutional knowledge and applies it consistently to every contract that crosses your desk.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/demo"
                className="px-8 py-4 text-sm font-semibold bg-[#C8A84B] text-white rounded hover:bg-[#b8993f] transition-colors"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </Link>
              <a
                href="#value-props"
                className="px-8 py-4 text-sm font-semibold text-white border border-white/30 rounded hover:border-white/60 transition-colors"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROBLEM STATS ─── */}
      <section id="problem-stats" data-animate className="py-20 bg-[#1B3A6B]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-3 gap-12 transition-all duration-700 ${show("problem-stats") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {[
              { stat: "60-80%", label: "of associate time spent on contract review", delay: "0ms" },
              { stat: "$250K+", label: "cost to replace a departing associate", delay: "150ms" },
              { stat: "5-15", label: "revision cycles per negotiated agreement", delay: "300ms" },
            ].map((card, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-700 ${show("problem-stats") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
                style={{ transitionDelay: card.delay }}
              >
                <p className="text-5xl font-bold text-[#C8A84B] mb-2">{card.stat}</p>
                <p className="text-sm text-white/60 uppercase tracking-wide" style={{ fontFamily: "system-ui, sans-serif" }}>{card.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VALUE PROPS ─── */}

      {/* 1. Institutional Knowledge */}
      <section id="value-props" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show("value-props") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-xl overflow-hidden shadow-xl border border-gray-100">
              <img src="/images/law-firms-library.webp" alt="Law firm library" className="w-full h-72 object-cover" loading="lazy" />
            </div>
            <div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Playbook Intelligence</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-6">
                Institutional knowledge<br />
                <span className="text-[#C8A84B]">that stays.</span>
              </h2>
              <div className="space-y-4 text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                <p>Your best associate just gave notice. Their instinct for spotting bad clauses walks out the door.</p>
                <p>Contract Lucidity&apos;s playbook captures your firm&apos;s position standards, risk thresholds, clause templates, and terminology.</p>
                <p>Every analysis applies these standards automatically. New associates get the benefit of decades of deal experience from their first week.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Revision Cycles */}
      <section id="revision-cycles" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show("revision-cycles") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="order-2 md:order-1">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Redline Intelligence</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-6">
                Revision cycles<br />
                <span className="text-[#C8A84B]">without re-work.</span>
              </h2>
              <div className="space-y-4 text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                <p>Opposing counsel sends back a marked-up draft. Your associate starts from scratch comparing versions.</p>
                <p>With Contract Lucidity: upload the revision, get an instant full-document redline, and the AI validates each clause analysis against the changes. Unchanged findings carry forward. Only impacted clauses get re-evaluated.</p>
                <p>Download the redline as DOCX. Send it to the partner in the format they expect.</p>
              </div>
            </div>
            <div className="order-1 md:order-2 rounded-xl overflow-hidden shadow-xl border border-gray-100">
              <img src="/images/law-firms-associates.webp" alt="Associates reviewing documents" className="w-full h-72 object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* 3. From Analysis to Calendar */}
      <section id="calendar" data-animate className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`text-center transition-all duration-700 ${show("calendar") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Obligation Tracking</p>
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-6">
              From analysis<br />
              <span className="text-[#C8A84B]">to calendar.</span>
            </h2>
            <div className="max-w-2xl mx-auto space-y-4 text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
              <p>Obligations extracted automatically with deadlines and responsible parties.</p>
              <p>Export directly to Outlook or Google Calendar with RFC 5545 compliance — recurring events, rich metadata, clickable links back to the document.</p>
              <p>Never miss a renewal date, cure period, or compliance deadline.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIAL-STYLE QUOTE ─── */}
      <section id="quote" data-animate className="py-20 bg-[#1B3A6B]">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("quote") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center">
              <div className="inline-block h-px w-16 bg-[#C8A84B] mb-8" />
              <blockquote className="text-2xl md:text-3xl text-white leading-snug mb-8">
                &ldquo;Upload a 40-page MSA. Get a clause-by-clause analysis in 5 minutes. Review each finding. Generate a Word document with redlined revisions. Send it to opposing counsel today.&rdquo;
              </blockquote>
              <div className="inline-block h-px w-16 bg-[#C8A84B]" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── ENTERPRISE IDENTITY ─── */}
      <section id="enterprise-identity" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("enterprise-identity") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-[#1B3A6B] rounded-lg p-10 text-center">
              <h3 className="text-2xl text-white mb-6">Enterprise Identity</h3>
              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {[
                  "OIDC Single Sign-On and SCIM 2.0 provisioning for seamless firm-wide deployment",
                  "Role-based access control to manage associate and partner access to projects",
                  "Group-based project access — assign practice groups to the matters they work on",
                  "Self-hosted deployment with your existing identity provider (Azure AD, Okta, Google Workspace)",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-left">
                    <span className="text-[#C8A84B] mt-0.5 shrink-0">&#10003;</span>
                    <p className="text-sm text-white/70 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ETHICAL WALLS ─── */}
      <section id="ethical-walls" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("ethical-walls") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-6">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Information Barriers</p>
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">Ethical Walls Built for Law Firms</h2>
              <p className="text-gray-500 max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Matter-level information barriers enforced at the platform level. Designed for ABA Model Rule 1.10 compliance so conflicts counsel can screen with confidence.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {[
                { title: "Matter-Level Barriers", detail: "Conflicts counsel creates a wall, assigns barred attorneys, and assigns restricted projects. Enforcement is immediate across all documents in those matters." },
                { title: "Deny-Overrides-Allow", detail: "A deny rule takes absolute precedence over any allow grant. No combination of role, group, or project membership can override a wall — eliminating accidental exposure." },
                { title: "No Admin Bypass", detail: "Even firm administrators cannot view walled content. The barrier is enforced at the database query level with no override or escalation path." },
                { title: "Audit Trail for Defense", detail: "Every access decision is logged with user, resource, wall rule, and timestamp. Export the full trail for court filings or regulatory inquiries." },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`bg-[#F8F9FB] rounded-lg p-8 border border-gray-100 transition-all duration-700 ${
                    show("ethical-walls") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <h3 className="text-lg text-[#1B3A6B] mb-2" style={{ fontFamily: "Georgia, serif" }}>{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── LUCID VAULT CALLOUT ─── */}
      <section className="py-16 bg-[#F8F9FB]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-xl p-8 md:p-10 border-2 border-[#C8A84B] flex flex-col md:flex-row items-center gap-8">
            <div className="shrink-0">
              <span className="w-14 h-14 rounded-full bg-[#C8A84B] text-white flex items-center justify-center text-lg font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>LV</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl text-[#1B3A6B] mb-2">How do your clients send contracts securely?</h3>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                Lucid Vault gives your firm a branded, encrypted document portal. Clients upload contracts through your domain, your team reviews them in Contract Lucidity. End-to-end security with full audit trail.
              </p>
            </div>
            <a
              href="https://lucidvault.contractlucidity.com"
              rel="noopener noreferrer"
              className="shrink-0 px-6 py-3 text-sm font-semibold bg-[#C8A84B] text-white rounded hover:bg-[#b8993f] transition-colors whitespace-nowrap"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Explore Lucid Vault
            </a>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" data-animate className="py-24 bg-[#C8A84B]">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                See what your contracts are really saying.
              </h2>
              <p className="text-gray-500 mb-10 max-w-xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Upload a real contract. Get clause-by-clause analysis with your firm&apos;s standards applied. Generate a revised document with redlined revisions. All in under 5 minutes.
              </p>
              <a
                href="/demo"
                className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
